import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Badge, Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Message from '../components/Message';
import Loader from '../components/Loader';

function CustomizationListScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [customizations, setCustomizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        const fetchCustomizations = async () => {
            try {
                setLoading(true);
                const config = {
                    headers: {
                        Authorization: `Bearer ${userInfo.token}`,
                    },
                };

                const { data } = await axios.get('/api/orders/', config);
                
                // Filter orders that have customization data
                const ordersWithCustomization = data.filter(
                    order => order.customization && !order.customization.skipped
                );
                
                setCustomizations(ordersWithCustomization);
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.detail || err.message);
                setLoading(false);
            }
        };

        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
        } else {
            fetchCustomizations();
        }
    }, [navigate, userInfo]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div>
            <h1>{t('customization.title')}</h1>
            <p className="text-muted mb-4">
                {t('customization.subtitle')}
            </p>

            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant='danger'>{error}</Message>
            ) : customizations.length === 0 ? (
                <Message variant='info'>
                    {t('customization.empty')}
                </Message>
            ) : (
                <Table striped bordered hover responsive className='table-sm'>
                    <thead>
                        <tr>
                            <th>{t('customization.colOrderId')}</th>
                            <th>{t('customization.colDate')}</th>
                            <th>{t('customization.colCustomer')}</th>
                            <th>{t('customization.colChildName')}</th>
                            <th>{t('customization.colAge')}</th>
                            <th>{t('customization.colTheme')}</th>
                            <th>{t('customization.colStatus')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {customizations.map((order) => (
                            <tr key={order.OrderId}>
                                <td>#{order.OrderId}</td>
                                <td>{formatDate(order.created_at)}</td>
                                <td>{order.user?.name || 'N/A'}</td>
                                <td>{order.customization?.childName || 'N/A'}</td>
                                <td>{order.customization?.childAge || 'N/A'}</td>
                                <td>{order.customization?.storyTheme || 'N/A'}</td>
                                <td>
                                    {order.is_delivered ? (
                                        <Badge bg="success">{t('customization.delivered')}</Badge>
                                    ) : order.is_paid ? (
                                        <Badge bg="warning" text="dark">{t('customization.processing')}</Badge>
                                    ) : (
                                        <Badge bg="secondary">{t('customization.awaitingPayment')}</Badge>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {customizations.length > 0 && (
                <Card className="mt-4">
                    <Card.Body>
                        <Card.Title>{t('customization.summary')}</Card.Title>
                        <p><strong>{t('customization.totalRequests')}</strong> {customizations.length}</p>
                        <p><strong>{t('customization.pendingProcessing')}</strong> {customizations.filter(o => o.is_paid && !o.is_delivered).length}</p>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default CustomizationListScreen;
